import React, { useRef, memo } from 'react';
import { useTranslation } from 'react-i18next';

interface QuickPrompt {
  text: string;
  type: 'query' | 'create' | 'delete' | 'update' | 'help';
}

interface IOSQuickPromptsProps {
  onSelectPrompt: (prompt: string) => void;
}

/**
 * iOS 风格的快速提示组件
 * 参考 iOS Messages 应用的建议回复设计
 */
const IOSQuickPromptsComp: React.FC<IOSQuickPromptsProps> = ({ onSelectPrompt }) => {
  const { t } = useTranslation(['v3/chat', 'v3/common']);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const quickPrompts: QuickPrompt[] = [
    // 查询类提示
    {
      text: t('v0/chat:quickPrompts.pendingTasks', '我有哪些待处理的任务？'),
      type: 'query'
    },
    {
      text: t('v0/chat:quickPrompts.upcomingEvents', '接下来几天有什么活动？'),
      type: 'query'
    },
    {
      text: t('v0/chat:quickPrompts.summarizeDay', '总结一下我今天的安排'),
      type: 'query'
    },

    // 创建类提示
    {
      text: t('v0/chat:quickPrompts.createMeeting', '安排一个新的会议'),
      type: 'create'
    },
    {
      text: t('v0/chat:quickPrompts.addTask', '添加一个打豆豆任务'),
      type: 'create'
    },

    // 更新类提示
    {
      text: t('v0/chat:quickPrompts.reschedule', '重新安排下午的活动'),
      type: 'update'
    },

    // 删除类提示
    {
      text: t('v0/chat:quickPrompts.deleteTask', '删除打豆豆任务'),
      type: 'delete'
    }
  ];

  return (
    <div className="w-full px-0 py-2">
      {/* 快速提示滚动容器 - 只负责滚动 */}
      <div
        ref={scrollContainerRef}
        className="overflow-x-auto hide-scrollbar pb-1"
      >
        {/* 内部 Flex 容器 - 负责布局、gap 和首尾 padding */}
        <div className="flex gap-2 px-2">
          {quickPrompts.map((prompt, index) => (
            <button
              key={index}
              onClick={() => onSelectPrompt(prompt.text)}
              className={`
                flex items-center whitespace-nowrap px-3 py-1.5 rounded-2xl min-w-max
                text-sm font-medium transition-colors duration-150
                bg-ios-gray5 dark:bg-ios-dark-gray4
                text-ios-black dark:text-white
              `}
            >
              <span>{prompt.text}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 隐藏滚动条样式 */}
      <style>
        {`
          .hide-scrollbar {
            -ms-overflow-style: none;  /* IE and Edge */
            scrollbar-width: none;     /* Firefox */
          }
          .hide-scrollbar::-webkit-scrollbar {
            display: none;  /* Chrome, Safari, Opera */
          }
        `}
      </style>
    </div>
  );
};

export const IOSQuickPrompts = memo(IOSQuickPromptsComp);
export default IOSQuickPrompts;
